/**
 * Inline identity editor — opens as a small panel inside the
 * dashboard row or workspace header. Saves via PATCH /v1/estimates/{id}
 * with optimistic-version concurrency. On 409 the UI prompts the user
 * to refresh and try again.
 */

import { useState, type FormEvent } from "react";

import { deleteEstimate, patchEstimate } from "@starz/shared/api/sdk.gen";
import type { Estimate, RoomType } from "@starz/shared/api/types.gen";
import { ROOM_TYPE_LABEL } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  onSaved: (updated: Estimate) => void;
  onCancel: () => void;
  /** When true, only show room_label + room_type. Address fields hidden. */
  compact?: boolean;
  /** Optional — when provided AND the estimate is still in_progress,
   * a Delete button surfaces. Mirrors mobile's `EditProjectDetailsModal`
   * delete affordance for surface parity. */
  onDeleted?: () => void;
}

const ROOM_TYPES: RoomType[] = [
  "kitchen",
  "bathroom",
  "bedroom",
  "living_room",
  "garage",
  "whole_house",
  "other",
];

export function IdentityEditor({
  estimate,
  onSaved,
  onCancel,
  compact = false,
  onDeleted,
}: Props) {
  const [roomLabel, setRoomLabel] = useState(estimate.room_label ?? "");
  const [roomType, setRoomType] = useState<RoomType | "">(estimate.room_type ?? "");
  const [line1, setLine1] = useState(estimate.address?.line1 ?? "");
  const [line2, setLine2] = useState(estimate.address?.line2 ?? "");
  const [city, setCity] = useState(estimate.address?.city ?? "");
  const [state, setState] = useState(estimate.address?.state ?? "");
  const [zip, setZip] = useState(estimate.address?.zip ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror mobile's rule: only allow soft-delete while the project is
  // still in_progress. After submission the customer must work with
  // their contractor to cancel a project (audit trail).
  const canDelete = onDeleted !== undefined && estimate.status === "in_progress";

  async function onDeleteClick() {
    if (!canDelete) return;
    if (
      !window.confirm(
        "Delete this project? This can't be undone — your chat history and photos will be removed from your project list.",
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    const result = await deleteEstimate({ estimateId: estimate.estimate_id });
    setDeleting(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onDeleted?.();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const body: Parameters<typeof patchEstimate>[0]["body"] = {
      room_label: roomLabel.trim() || null,
      room_type: (roomType || null) as RoomType | null,
    };
    if (!compact) {
      // Include address only when fully provided (or fully blank → null).
      const anyAddress = line1 || line2 || city || state || zip;
      if (anyAddress && line1 && city && state && zip) {
        body.address = {
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
        };
      } else if (!anyAddress) {
        body.address = null;
      }
    }

    const result = await patchEstimate({
      estimateId: estimate.estimate_id,
      ifMatchVersion: estimate.version,
      body,
    });

    setSaving(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onSaved(result.data);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Project name
        </label>
        <input
          type="text"
          value={roomLabel}
          maxLength={80}
          onChange={(e) => setRoomLabel(e.target.value)}
          placeholder="e.g. Master bath remodel"
          className="mt-1 w-full rounded-lg border border-starz-gold/20 bg-white px-4 py-2 text-base focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Room type
        </label>
        <select
          value={roomType}
          onChange={(e) => setRoomType(e.target.value as RoomType | "")}
          className="mt-1 w-full rounded-lg border border-starz-gold/20 bg-white px-4 py-2 text-base focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40"
        >
          <option value="">— Select —</option>
          {ROOM_TYPES.map((rt) => (
            <option key={rt} value={rt}>
              {ROOM_TYPE_LABEL[rt]}
            </option>
          ))}
        </select>
      </div>

      {!compact && (
        <fieldset className="space-y-3 rounded-lg border border-starz-gold/15 bg-starz-cream/40 p-4">
          <legend className="px-2 text-xs font-semibold uppercase tracking-widest text-starz-gold">
            Project address (optional)
          </legend>
          <input
            type="text"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            placeholder="Street address"
            className="w-full rounded-md border border-starz-gold/20 bg-white px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            placeholder="Apt / Suite (optional)"
            className="w-full rounded-md border border-starz-gold/20 bg-white px-3 py-2 text-sm"
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="col-span-2 rounded-md border border-starz-gold/20 bg-white px-3 py-2 text-sm md:col-span-1"
            />
            <input
              type="text"
              value={state}
              maxLength={2}
              onChange={(e) => setState(e.target.value.toUpperCase())}
              placeholder="State"
              className="rounded-md border border-starz-gold/20 bg-white px-3 py-2 text-sm uppercase"
            />
            <input
              type="text"
              value={zip}
              maxLength={5}
              pattern="\d{5}"
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="ZIP"
              className="rounded-md border border-starz-gold/20 bg-white px-3 py-2 text-sm"
            />
          </div>
        </fieldset>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error.includes("version") ? (
            <>This estimate changed elsewhere. Please refresh and try again.</>
          ) : (
            error
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {canDelete ? (
          <button
            type="button"
            onClick={() => void onDeleteClick()}
            disabled={saving || deleting}
            className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40"
          >
            {deleting ? "Deleting…" : "Delete project"}
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving || deleting}
            className="rounded-md border border-starz-gold/30 bg-white px-4 py-2 text-sm font-medium text-starz-charcoal hover:bg-starz-cream"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || deleting}
            className="rounded-md bg-starz-navy px-5 py-2 text-sm font-semibold text-starz-off-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
}
